/**
 * Socket.IO client singleton (spec §3.3). Same origin, path `/socket.io`; the Vite dev server
 * proxies it (incl. WebSocket upgrades) to the game server.
 *
 * The socket is created lazily with `autoConnect: false`; the store (`state/store.ts`) binds the
 * listeners and decides when to connect. Calls use `emitWithAck`, which never rejects: it resolves
 * with the server ack or a client-side `{ ok: false, error: 'TIMEOUT' }`.
 */
import type { AckFn, ClientToServerEvents, ServerToClientEvents } from '@20fight/server/protocol';
import { io, type Socket } from 'socket.io-client';
import type { AppErrorCode } from './errors';

export type GameSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

/** Ack timeout for every client → server call. */
export const ACK_TIMEOUT_MS = 8000;

export type ClientEvent = keyof ClientToServerEvents;
export type EventPayload<E extends ClientEvent> = Parameters<ClientToServerEvents[E]>[0];
/** Extra fields of the success ack of event `E` (e.g. `{ code }` for `room:create`). */
export type AckData<E extends ClientEvent> =
  Parameters<ClientToServerEvents[E]>[1] extends AckFn<infer T> ? T : never;

export type ClientAckError = { ok: false; error: AppErrorCode; detail?: string };
export type ClientAck<T extends object = object> = ({ ok: true } & T) | ClientAckError;

let socket: GameSocket | null = null;

export function getSocket(): GameSocket {
  if (!socket) {
    socket = io({
      path: '/socket.io',
      autoConnect: false,
      // WebSocket first; fall back to HTTP long-polling when WebSockets are blocked.
      transports: ['websocket', 'polling'],
      tryAllTransports: true,
      reconnectionDelay: 500,
      reconnectionDelayMax: 4000,
      randomizationFactor: 0.3,
    });
  }
  return socket;
}

/** Test helper: forget the singleton (the next `getSocket()` creates a new one). */
export function _resetSocket(): void {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
  }
  socket = null;
}

function normalizeAck(res: unknown): ClientAck<Record<string, unknown>> {
  if (res !== null && typeof res === 'object' && 'ok' in res) {
    const r = res as { ok: unknown; error?: unknown; detail?: unknown };
    if (r.ok === true) return res as { ok: true } & Record<string, unknown>;
    const error = typeof r.error === 'string' ? (r.error as AppErrorCode) : 'UNKNOWN';
    return typeof r.detail === 'string'
      ? { ok: false, error, detail: r.detail }
      : { ok: false, error };
  }
  return { ok: false, error: 'UNKNOWN' };
}

type TimedEmit = (
  event: string,
  payload: unknown,
  ack: (err: Error | null, res?: unknown) => void,
) => void;

/**
 * Emit `event` and wait (at most `timeoutMs`) for its ack. Never rejects.
 *
 * Uses socket.io's own `timeout()` so an unanswered ack is dropped from the client's ack table and
 * a pending ack fails right away (`OFFLINE`) when the connection drops instead of waiting for the
 * timer; a packet still buffered when the timer fires is discarded rather than sent later.
 */
export function emitWithAck<E extends ClientEvent>(
  event: E,
  payload: EventPayload<E>,
  timeoutMs: number = ACK_TIMEOUT_MS,
): Promise<ClientAck<AckData<E>>> {
  const s = getSocket();
  return new Promise((resolve) => {
    let settled = false;
    const done = (ack: ClientAck<Record<string, unknown>>): void => {
      if (settled) return;
      settled = true;
      resolve(ack as ClientAck<AckData<E>>);
    };
    const timed = s.timeout(timeoutMs) as unknown as { emit: TimedEmit };
    timed.emit(event, payload, (err, res) => {
      if (err) done({ ok: false, error: /timed out/i.test(err.message) ? 'TIMEOUT' : 'OFFLINE' });
      else done(normalizeAck(res));
    });
  });
}

/**
 * Connect if needed and wait until connected. Resolves `false` after `timeoutMs` (the socket keeps
 * trying in the background).
 */
export function ensureConnected(timeoutMs: number = ACK_TIMEOUT_MS): Promise<boolean> {
  const s = getSocket();
  if (s.connected) return Promise.resolve(true);
  return new Promise((resolve) => {
    const onConnect = (): void => {
      clearTimeout(timer);
      resolve(true);
    };
    const timer = setTimeout(() => {
      s.off('connect', onConnect);
      resolve(false);
    }, timeoutMs);
    s.once('connect', onConnect);
    if (!s.active) s.connect();
  });
}

// Dev HMR: drop the old socket so a re-evaluated module does not leave a second connection open.
if (import.meta.hot) import.meta.hot.dispose(() => _resetSocket());
